export type ObjectSubTypeOption = {
  id: number | string
  name?: string
  parent_invent_type_id?: number
}

/** Метаданные строки catalog_invent_objects_type для загрузки subtypes. */
export type CatalogTypeMeta = {
  catalogRowId: number
  objectTypeId: number
  isDictionaryRoot: boolean
}

/**
 * Парсит ответ GET /inventory/group/:groupId/object-types/:typeRef.
 * `typeRef` может быть id строки справочника (26342) или object_type_id (169).
 */
export function parseCatalogTypeMeta(raw: unknown, fallbackTypeId: number): CatalogTypeMeta {
  const root = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const data =
    root.data != null && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : root

  const catalogRowId = Number(data.id)
  const objectTypeNested = data.object_type
  const objectTypeId = Number(
    objectTypeNested != null && typeof objectTypeNested === 'object'
      ? (objectTypeNested as Record<string, unknown>).id
      : data.object_type_id,
  )

  return {
    catalogRowId: Number.isFinite(catalogRowId) && catalogRowId > 0 ? catalogRowId : fallbackTypeId,
    objectTypeId: Number.isFinite(objectTypeId) && objectTypeId > 0 ? objectTypeId : fallbackTypeId,
    isDictionaryRoot: Number(data.list_invent_object_type_id) === -1,
  }
}

/** Для корневого справочника оставляет только записи с parent = catalogRowId. */
export function filterSubTypesForCatalog(
  subTypes: ObjectSubTypeOption[],
  meta: CatalogTypeMeta,
): ObjectSubTypeOption[] {
  if (!meta.isDictionaryRoot) return subTypes
  return subTypes.filter((row) => Number(row.parent_invent_type_id) === meta.catalogRowId)
}

/** Проверяет, что значение селекта действительно пустое. */
export const isEmptySelectValue = (value: unknown): boolean => value == null || value === ''

/**
 * Сигнальное значение опции «пусто» в селектах list / list_types.
 * Не сохраняется в модель — при выборе отображает заглушку и сбрасывает поле в null.
 */
export const NULL_SELECT_VALUE = '__null__'

/** Подпись опции «пусто» — визуальный плейсхолдер «нет значения». */
export const NULL_SELECT_TITLE = '-'

/** Ищет подтип объекта с учетом разных форматов id из API и модели. */
export const findObjectSubTypeOption = (
  items: ObjectSubTypeOption[],
  id: unknown,
): ObjectSubTypeOption | undefined => {
  if (!items.length || isEmptySelectValue(id)) return undefined
  return items.find((item) => String(item.id) === String(id))
}

/**
 * Значение для селекта: нормализует тип id под options.
 * Если опция ещё не в списке (модалка только что применила id, options грузятся) —
 * оставляем rawValue, чтобы поле не очищалось визуально.
 */
export const resolveObjectSubTypeDisplayValue = (
  rawValue: unknown,
  items: ObjectSubTypeOption[],
): unknown => {
  if (isEmptySelectValue(rawValue)) return null
  const matched = findObjectSubTypeOption(items, rawValue)
  if (matched && matched.id !== rawValue) return matched.id
  return rawValue
}
